// Shared API error message extraction (LitigAI implementation, the superset; SeriesAI
// GraphQL protocol-key filter folded in 2026-10-07).
import { describeHttpStatus, humanizeErrorMessage } from "../utils/humanizeError";

const isObjectLike = (value) => value !== null && typeof value === "object";

// Fields a GraphQL server sends for machines, never for a person to read.
const GRAPHQL_PROTOCOL_KEYS = new Set([
  "extensions",
  "locations",
  "path",
  "stacktrace",
  "traceback",
]);


const normalizeMessage = (message) => {
  if (typeof message !== "string") return "";
  return message.replace(/\s+/g, " ").trim();
};

const pushString = (bucket, value) => {
  const normalized = normalizeMessage(value);
  if (normalized) bucket.push(normalized);
};

const collectMessages = (value, bucket, fieldPrefix) => {
  if (value === null || value === undefined) return;

  if (typeof value === "string" || typeof value === "number") {
    const stringValue = typeof value === "string" ? value : String(value);
    pushString(bucket, fieldPrefix ? `${fieldPrefix}: ${stringValue}` : stringValue);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => collectMessages(entry, bucket, fieldPrefix));
    return;
  }

  if (!isObjectLike(value)) return;

  // Common DRF / API patterns
  if (typeof value.detail === "string") pushString(bucket, value.detail);
  if (typeof value.message === "string") pushString(bucket, value.message);
  if (typeof value.error === "string") pushString(bucket, value.error);
  // A machine code alone ("sharepoint_api_error") goes through bare, so the
  // humanizer rules can translate it or drop it as noise. Beside a readable
  // message it adds nothing and used to leak as "code: ...".
  if (typeof value.code === "string" && typeof value.message !== "string" && typeof value.detail !== "string") {
    pushString(bucket, value.code);
  }
  if (Array.isArray(value.non_field_errors)) {
    value.non_field_errors.forEach((entry) => collectMessages(entry, bucket));
  }
  if (Array.isArray(value.errors)) {
    value.errors.forEach((entry) => collectMessages(entry, bucket));
  }

  Object.entries(value).forEach(([key, entry]) => {
    if (
      key === "detail" ||
      // Handled above: never shown as "code: ...".
      key === "code" ||
      key === "message" ||
      key === "error" ||
      key === "non_field_errors" ||
      key === "errors" ||
      // GraphQL protocol metadata, not prose for a person. Walking it turned a
      // 401 into the toast "Authentication required. Provide valid JWT token
      // auth header. extensions.code: UNAUTHENTICATED" -- the generic walker
      // flattened `extensions.code` into a `<path>: <value>` string and the
      // humanizer had no rule to drop it. `locations`/`path` leak the same way.
      GRAPHQL_PROTOCOL_KEYS.has(key)
    ) {
      return;
    }

    const prefix = fieldPrefix ? `${fieldPrefix}.${key}` : key;
    collectMessages(entry, bucket, prefix);
  });
};

export const getApiErrorMessage = (
  error,
  fallbackMessage = "Something went wrong. Please try again.",
) => {
  const messages = [];

  // Several API wrappers (signatures' buildFailure among them) already mine
  // the axios error and hand back the resolved message as a plain string.
  // Without this branch that string matched neither `response.data` nor
  // `message`, so an actionable backend detail like "Access denied for caseId
  // ..." was silently replaced by the caller's generic fallback.
  if (typeof error === "string") {
    pushString(messages, error);
  }

  if (messages.length === 0) {
    const data = error?.response?.data;
    collectMessages(data, messages);
  }

  if (messages.length === 0) {
    pushString(messages, error?.message);
  }

  // Rewrite each raw DRF / Cognito / axios string into something a
  // non-technical user can act on, and drop the ones that are pure
  // developer noise rather than pasting them into the UI.
  const humanized = messages
    .map((message) => humanizeErrorMessage(message))
    .filter(Boolean);

  const seen = new Set();
  const unique = humanized.filter((message) => {
    const key = message.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const trimmed = unique.map((message) => message.slice(0, 300)).filter(Boolean);

  if (trimmed.length === 0) {
    return describeHttpStatus(error?.response?.status, fallbackMessage);
  }

  // Two is enough for a toast; more reads as a wall of text.
  return trimmed.slice(0, 2).join(" ");
};

